/**
 * «Asignación inicial» — the words for the reason codes a run writes are the PLATFORM's (`GET /launch/initial/reason-labels`, platform
 * ADR 0057): one list per place a code is written — what bound a line, why an article is not allocated, why a store was left out of the
 * run or posed and given nothing, what an article lacks, an article's notes. This build carried catalogues of its own for them; two
 * sources of text for one thing is how a screen and a file come to disagree, so they are gone. What stays in `./words.tsx` is what is
 * NOT a reason label: a run's verdicts, the price basis, the states of a job.
 *
 * A code the platform does not label is `null` — the caller says «Motivo no descrito en esta versión» and keeps the code behind «id»
 * (ADR 9060). While the labels are on their way a code reads `''`: a page draws nothing for a reason rather than the sentence that says
 * there are no words for it.
 */

import type { ReasonLabels } from '@/api';
import { NO_REASON_LABELS } from '@/api';

import { splitNote } from './words';

/** What the words need of a translator: a screen's `t` or a file's. */
export type Say = (key: string, values?: Readonly<Record<string, string | number>>) => string;

/** The places a reason code is written, as the platform names them. */
export const REASON_GROUP = {
  /** What bound a line (`reason_code` of a line). */
  line: 'lineReason',
  /** Why an article the DC holds is not «to allocate». */
  selection: 'selectionReason',
  /** Why a store was left out of the run. */
  leftOut: 'leftOutReason',
  /** Why a store was posed to the engine and sent nothing. */
  notServed: 'notServedReason',
  /** An input an article lacks, beyond the objective's own terms. */
  missing: 'missingInput',
  /** What a run notes about an article's inputs. */
  note: 'note',
  /** Why a line has no verdict on its store's shipment. */
  shipment: 'shipmentReason',
  /** Why a line has no «valid until». */
  validUntil: 'validUntilReason',
  /** Why a decided line has no market day of decision. */
  marketDay: 'marketDayReason',
  /** Why a similar article has no share at a line's store. */
  similarShare: 'similarShareReason',
} as const;

/** No labels read: every code is one this build has no words for. */
export const NO_REASON_WORDS_LABELS = NO_REASON_LABELS;

export interface ReasonWords {
  /** Whether the labels have settled (arrived or failed). */
  readonly loaded: boolean;
  /** What bound a line, in words; `null` for a code with none; `''` while the labels are on their way. */
  readonly line: (code: string | null | undefined) => string | null;
  /** The constraints the platform lists for a line, in its order (the filter offers them). */
  readonly lineCodes: () => readonly string[];
  readonly selection: (code: string) => string | null;
  /** Why a line has no verdict on its store's shipment (the minimum is not stated, the market's clock is not set…). */
  readonly shipment: (code: string | null | undefined) => string | null;
  /** Why a line has no «valid until» (not decided, deferred, rejected, not committed). */
  readonly validUntil: (code: string | null | undefined) => string | null;
  /** Why a decision has no market day (the market has no time zone, or nobody decided). */
  readonly marketDay: (code: string | null | undefined) => string | null;
  /** Why a similar article has no share at the line's store. */
  readonly similarShare: (code: string | null | undefined) => string | null;
  /** An input an article lacks — the objective's terms, the price and cost reads, the quantity bought: all the platform's words. */
  readonly missing: (code: string) => string | null;
  /** Why a cell was excluded or not served — the two lists the platform writes it in. */
  readonly cell: (code: string | null | undefined) => string | null;
  /** A note, with its count where it carries one. */
  readonly note: (note: string, count?: (count: number) => string) => string | null;
  /** Several codes in words, joined by «; »; a code with no words reads as the unclassified sentence once. */
  readonly list: (codes: readonly string[], of: (code: string) => string | null) => string;
}

/** The words of an app's reason codes over its labels. Pure. */
export function reasonWordsFrom(t: Say, labels: ReasonLabels): ReasonWords {
  const pending = (code: string | null | undefined): '' | undefined => (code === null || code === undefined || !labels.loaded ? '' : undefined);
  // A read that failed is one sentence for every code, in words (`launch.initial.reasonsUnreadable`) — never the code.
  const lookup = (group: string, code: string | null | undefined): string | null => (
    labels.failed && code !== null && code !== undefined && code !== '' ? t('launch.initial.reasonsUnreadable') : pending(code) ?? labels.labelOrNull(group, code)
  );
  return {
    loaded: labels.loaded,
    line: (code) => lookup(REASON_GROUP.line, code),
    lineCodes: () => labels.codes(REASON_GROUP.line),
    selection: (code) => lookup(REASON_GROUP.selection, code),
    shipment: (code) => lookup(REASON_GROUP.shipment, code),
    validUntil: (code) => lookup(REASON_GROUP.validUntil, code),
    marketDay: (code) => lookup(REASON_GROUP.marketDay, code),
    similarShare: (code) => lookup(REASON_GROUP.similarShare, code),
    missing: (code) => lookup(REASON_GROUP.missing, code),
    cell: (code) => (labels.failed && code !== null && code !== undefined && code !== '' ? t('launch.initial.reasonsUnreadable') : pending(code) ?? labels.labelOrNull(REASON_GROUP.leftOut, code) ?? labels.labelOrNull(REASON_GROUP.notServed, code)),
    note: (note, count) => {
      const parsed = splitNote(note);
      const label = lookup(REASON_GROUP.note, parsed.code);
      if (label === null || label === '' || parsed.count === null) return label;
      return t('launch.initial.note.counted', { note: label, count: count === undefined ? String(parsed.count) : count(parsed.count) });
    },
    list: (codes, of) => {
      const known = codes.map(of).filter((word): word is string => word !== null && word !== '');
      const unknown = codes.filter((code) => of(code) === null).length;
      return [...known, ...(unknown > 0 ? [t('launch.initial.unclassified')] : [])].join('; ');
    },
  };
}
